<template>
  <PageLayout :title="pageTitle" :subtitle="pageSubtitle">
    <template #actions>
      <button
        v-if="canPublish"
        class="btn btn-success mr-2"
        :disabled="publishing"
        @click="onPublish"
      >
        {{ publishing ? $t('common.please_wait') : $t('common.publish') }}
      </button>
      <button
        v-if="canDelete"
        class="btn btn-outline-danger mr-2"
        :disabled="deleting"
        @click="onDelete"
      >
        {{ deleting ? $t('common.please_wait') : $t('common.delete') }}
      </button>
      <router-link v-if="canEdit" :to="`/posts/${post.id}/edit`" class="btn btn-outline-primary mr-2">
        {{ $t('common.edit') }}
      </router-link>
      <router-link to="/posts" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
    </template>

    <div v-if="loading" class="state-loading" role="status">{{ $t('common.loading') }}</div>
    <div v-else-if="post" class="post-detail">
      <div v-if="post.status === 'publishing'" class="alert alert-info mb-3" role="status">
        <i class="fas fa-spinner fa-spin mr-2"></i>{{ $t('posts.publish_queued_detail') }}
      </div>

      <div v-if="analytics" class="post-detail__stats row mb-3">
        <div v-for="m in metrics" :key="m.key" class="col-6 col-md-4 col-lg-2 mb-2">
          <div class="post-stat">
            <i :class="m.icon" class="post-stat__icon"></i>
            <div class="post-stat__value">{{ formatNum(m.value) }}</div>
            <div class="post-stat__label">{{ m.label }}</div>
          </div>
        </div>
      </div>

      <div class="row post-detail__row">
        <div class="col-lg-7 order-lg-1 order-2">
          <ContentCard>
            <div class="post-detail__hero">
              <div class="post-detail__badges">
                <span class="badge badge-light text-capitalize">{{ post.type }}</span>
                <span class="badge" :class="statusClass">{{ statusLabel(post.status) }}</span>
                <span v-if="post.ai_generated" class="badge badge-info">{{ $t('posts.ai_badge') }}</span>
                <span v-if="post.recurring_post_id" class="badge badge-secondary">{{ $t('posts.recurring_badge') }}</span>
              </div>
              <router-link
                v-if="post.recurring_post_id"
                :to="`/recurring`"
                class="btn btn-xs btn-outline-secondary mt-2"
              >
                <i class="fas fa-redo mr-1"></i>{{ $t('posts.view_recurring') }}
              </router-link>
            </div>

            <h6 class="text-muted text-uppercase small font-weight-bold mb-2">{{ $t('posts.timeline_title') }}</h6>
            <ol class="post-timeline">
              <li v-for="step in timelineSteps" :key="step.key" :class="{ 'post-timeline__item--done': step.done }">
                <span class="post-timeline__dot"></span>
                <div>
                  <div class="post-timeline__label">{{ step.label }}</div>
                  <div class="post-timeline__date">{{ step.date }}</div>
                </div>
              </li>
            </ol>
          </ContentCard>

          <ContentCard card-class="mt-3">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
              <h5 class="mb-0">{{ $t('posts.content') }}</h5>
              <button
                v-if="post.caption"
                type="button"
                class="btn btn-xs btn-outline-secondary"
                @click="copyCaption"
              >
                <i class="far fa-copy mr-1"></i>{{ $t('posts.copy_caption') }}
              </button>
            </div>
            <p v-if="post.caption" class="post-caption mb-3">{{ post.caption }}</p>
            <p v-else class="text-muted mb-3">{{ $t('common.no_caption') }}</p>

            <div v-if="post.hashtags?.length" class="mb-3">
              <div class="small text-muted mb-2">{{ $t('composer.hashtags') }}</div>
              <span v-for="tag in post.hashtags" :key="tag" class="badge badge-secondary mr-1 mb-1">#{{ tag }}</span>
            </div>

            <div v-if="post.ai_prompt" class="post-ai-prompt mb-0">
              <div class="small text-muted mb-1"><i class="fas fa-magic mr-1"></i>{{ $t('common.topic') }}</div>
              <div>{{ post.ai_prompt }}</div>
            </div>
          </ContentCard>

          <ContentCard card-class="mt-3">
            <h5 class="mb-3">{{ $t('posts.details') }}</h5>

            <div class="post-account mb-4">
              <img
                v-if="post.social_account?.profile_picture_url"
                :src="post.social_account.profile_picture_url"
                class="post-account__avatar"
                alt=""
              />
              <div v-else class="post-account__avatar post-account__avatar--ph">
                <i class="fab fa-instagram"></i>
              </div>
              <div>
                <div class="font-weight-bold">@{{ post.social_account?.username || '-' }}</div>
                <div class="text-muted small">{{ post.social_account?.display_name || '-' }}</div>
                <span v-if="post.social_account?.platform" class="badge badge-light text-capitalize mt-1">
                  {{ post.social_account.platform }}
                </span>
              </div>
            </div>

            <dl class="post-meta row mb-0">
              <dt class="col-sm-5">{{ $t('posts.created') }}</dt>
              <dd class="col-sm-7">{{ formatDate(post.created_at) }}</dd>

              <dt class="col-sm-5">{{ $t('posts.updated') }}</dt>
              <dd class="col-sm-7">{{ formatDate(post.updated_at) }}</dd>

              <dt class="col-sm-5">{{ $t('posts.scheduled') }}</dt>
              <dd class="col-sm-7">
                {{ formatDate(post.scheduled_at) }}
                <router-link
                  v-if="post.scheduled_at"
                  :to="calendarLink"
                  class="btn btn-link btn-sm p-0 ml-1 align-baseline"
                >
                  {{ $t('posts.open_calendar') }}
                </router-link>
              </dd>

              <dt class="col-sm-5">{{ $t('posts.published_at') }}</dt>
              <dd class="col-sm-7">{{ formatDate(post.published_at) }}</dd>

              <dt v-if="post.meta_media_id" class="col-sm-5">{{ $t('posts.meta_media_id') }}</dt>
              <dd v-if="post.meta_media_id" class="col-sm-7 text-break"><code class="small">{{ post.meta_media_id }}</code></dd>

              <dt v-if="post.rejection_reason" class="col-sm-5">{{ $t('posts.rejection_reason') }}</dt>
              <dd v-if="post.rejection_reason" class="col-sm-7 text-danger">{{ post.rejection_reason }}</dd>

              <dt v-if="post.error_message" class="col-sm-5">{{ $t('posts.error') }}</dt>
              <dd v-if="post.error_message" class="col-sm-7 text-danger text-break">{{ post.error_message }}</dd>
            </dl>
          </ContentCard>

          <ContentCard v-if="analytics" card-class="mt-3">
            <h5 class="mb-2">{{ $t('posts.analytics') }}</h5>
            <small class="text-muted d-block mb-3">{{ $t('posts.synced_at', { date: formatDate(analytics.synced_at) }) }}</small>
            <router-link to="/analytics" class="btn btn-sm btn-outline-primary">{{ $t('nav.analytics') }}</router-link>
          </ContentCard>

          <ContentCard v-else-if="post.status === 'published'" card-class="mt-3">
            <p class="text-muted mb-2">{{ $t('posts.no_analytics') }}</p>
            <router-link to="/analytics" class="btn btn-sm btn-primary">{{ $t('posts.sync_analytics') }}</router-link>
          </ContentCard>

          <ContentCard card-class="mt-3">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
              <h5 class="mb-0">{{ $t('posts.comments_title') }}</h5>
              <button
                v-if="canLoadComments"
                type="button"
                class="btn btn-xs btn-outline-primary"
                :disabled="commentsLoading"
                @click="loadComments"
              >
                <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': commentsLoading }"></i>
                {{ commentsLoaded ? $t('posts.comments_refresh') : $t('posts.comments_load') }}
              </button>
            </div>

            <p v-if="!canLoadComments" class="text-muted small mb-0">{{ $t('posts.comments_unavailable') }}</p>

            <template v-else>
            <div class="post-comments__automation mb-3 pb-3 border-bottom">
              <h6 class="post-comments__section-title">{{ $t('posts.comment_automation_title') }}</h6>
              <p class="text-muted small mb-2">{{ $t('posts.comment_automation_hint') }}</p>
              <div class="custom-control custom-switch mb-2">
                <input
                  id="comment-auto-reply"
                  v-model="automationForm.enabled"
                  type="checkbox"
                  class="custom-control-input"
                />
                <label class="custom-control-label" for="comment-auto-reply">{{ $t('posts.comment_automation_enable') }}</label>
              </div>
              <div class="form-group mb-2">
                <label class="small mb-1">{{ $t('posts.comment_automation_message') }}</label>
                <input
                  v-model="automationForm.reply_message"
                  type="text"
                  class="form-control form-control-sm"
                  :placeholder="$t('posts.comment_automation_message_placeholder')"
                />
              </div>
              <div class="form-group mb-2">
                <label class="small mb-1">{{ $t('posts.comment_automation_keywords') }}</label>
                <input
                  v-model="automationForm.trigger_keywords"
                  type="text"
                  class="form-control form-control-sm"
                  :placeholder="$t('posts.comment_automation_keywords_placeholder')"
                />
                <small class="form-text text-muted">{{ $t('posts.comment_automation_keywords_hint') }}</small>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                :disabled="automationSaving"
                @click="saveCommentAutomation"
              >
                <i v-if="automationSaving" class="fas fa-spinner fa-spin mr-1"></i>
                {{ $t('posts.comment_automation_save') }}
              </button>
            </div>

            <div v-if="commentsLoading && !commentsPayload" class="text-muted small py-2">{{ $t('common.loading') }}</div>
            <p v-else-if="commentsError" class="text-danger small mb-2">{{ commentsError }}</p>

            <template v-else-if="commentsPayload">
              <h6 class="post-comments__section-title">{{ $t('posts.comments_analysis') }}</h6>
              <div class="row post-comments__analysis mb-3">
                <div class="col-6 col-md-4 mb-2">
                  <div class="post-comments__metric">
                    <strong>{{ commentsPayload.analysis.total }}</strong>
                    <span>{{ $t('posts.comments') }}</span>
                  </div>
                </div>
                <div class="col-6 col-md-4 mb-2">
                  <div class="post-comments__metric">
                    <strong>{{ commentsPayload.analysis.questions }}</strong>
                    <span>{{ $t('posts.analysis_questions') }}</span>
                  </div>
                </div>
                <div class="col-6 col-md-4 mb-2">
                  <div class="post-comments__metric">
                    <strong>{{ commentsPayload.analysis.avg_length }}</strong>
                    <span>{{ $t('posts.analysis_avg_length') }}</span>
                  </div>
                </div>
                <div class="col-6 col-md-4 mb-2">
                  <div class="post-comments__metric">
                    <strong>{{ commentsPayload.analysis.positive_estimate }}</strong>
                    <span>{{ $t('posts.analysis_positive') }}</span>
                  </div>
                </div>
                <div class="col-6 col-md-4 mb-2">
                  <div class="post-comments__metric">
                    <strong>{{ commentsPayload.analysis.negative_estimate }}</strong>
                    <span>{{ $t('posts.analysis_negative') }}</span>
                  </div>
                </div>
                <div class="col-6 col-md-4 mb-2">
                  <div class="post-comments__metric">
                    <strong>{{ commentsPayload.analysis.neutral_estimate }}</strong>
                    <span>{{ $t('posts.analysis_neutral') }}</span>
                  </div>
                </div>
              </div>
              <p class="text-muted small mb-3">{{ $t('posts.analysis_sentiment_hint') }}</p>

              <div v-if="commentsPayload.analysis.top_keywords?.length" class="mb-3">
                <div class="small font-weight-bold text-muted mb-1">{{ $t('posts.analysis_top_keywords') }}</div>
                <span
                  v-for="kw in commentsPayload.analysis.top_keywords"
                  :key="kw.word"
                  class="badge badge-light mr-1 mb-1"
                >
                  {{ kw.word }} ({{ kw.count }})
                </span>
              </div>

              <div v-if="commentsPayload.analysis.top_commenters?.length" class="mb-3">
                <div class="small font-weight-bold text-muted mb-1">{{ $t('posts.analysis_top_commenters') }}</div>
                <span
                  v-for="c in commentsPayload.analysis.top_commenters"
                  :key="c.username"
                  class="badge badge-secondary mr-1 mb-1"
                >
                  @{{ c.username }} ({{ c.count }})
                </span>
              </div>

              <div v-if="!commentsPayload.comments?.length" class="text-muted small">{{ $t('posts.comments_empty') }}</div>
              <ul v-else class="post-comments__list mb-0">
                <li v-for="item in commentsPayload.comments" :key="item.id" class="post-comments__item">
                  <div class="post-comments__item-head">
                    <span class="font-weight-bold">{{ item.username ? `@${item.username}` : $t('posts.comments') }}</span>
                    <span class="text-muted small">{{ formatCommentDate(item.timestamp) }}</span>
                  </div>
                  <p class="post-comments__text mb-1">{{ item.text }}</p>
                  <div v-if="replyingCommentId === item.id" class="post-comments__reply-form mt-2">
                    <input
                      v-model="replyMessage"
                      type="text"
                      class="form-control form-control-sm mb-1"
                      :placeholder="$t('posts.comment_reply_placeholder')"
                    />
                    <button
                      type="button"
                      class="btn btn-xs btn-primary mr-1"
                      :disabled="replySending"
                      @click="sendCommentReply(item.id)"
                    >
                      {{ $t('posts.comment_reply_send') }}
                    </button>
                    <button type="button" class="btn btn-xs btn-outline-secondary" @click="cancelCommentReply">
                      {{ $t('common.cancel') }}
                    </button>
                  </div>
                  <button
                    v-else
                    type="button"
                    class="btn btn-link btn-xs p-0"
                    @click="startCommentReply(item.id)"
                  >
                    {{ $t('posts.comment_reply') }}
                  </button>
                </li>
              </ul>
            </template>
            </template>
          </ContentCard>
        </div>

        <div class="col-lg-5 order-lg-2 order-1">
          <InstagramPreview
            :type="post.type"
            :username="previewUsername"
            :avatar-url="avatarUrl"
            :media-src="previewMediaSrc"
            :is-video="previewIsVideo"
            :caption="post.caption"
            :hashtags="post.hashtags || []"
            :likes-count="analytics?.likes"
            :comments-count="analytics?.comments"
          />

          <div v-if="hasMultipleMedia" class="post-detail__carousel">
            <div class="post-detail__carousel-head">
              <span class="small text-muted">
                {{ $t('posts.media_slide', { current: activeMediaIndex + 1, total: post.media_urls.length }) }}
              </span>
              <div class="post-detail__carousel-nav">
                <button type="button" class="btn btn-xs btn-outline-secondary" :disabled="activeMediaIndex === 0" @click="prevMedia">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-xs btn-outline-secondary"
                  :disabled="activeMediaIndex >= post.media_urls.length - 1"
                  @click="nextMedia"
                >
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
            <div class="post-detail__thumbs">
              <button
                v-for="(url, idx) in post.media_urls"
                :key="url + idx"
                type="button"
                class="post-detail__thumb"
                :class="{ 'post-detail__thumb--active': activeMediaIndex === idx }"
                @click="activeMediaIndex = idx"
              >
                <img v-if="isImageUrl(url)" :src="url" alt="" />
                <span v-else class="post-detail__thumb-video"><i class="fas fa-play"></i></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import { mapActions } from 'vuex'
import i18nPage from '@/mixins/i18nPage'
import InstagramPreview from '@/components/social/InstagramPreview.vue'
import { updateCommentAutomation, replyPostComment } from '@/api/posts'

const DEFAULT_AVATAR =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><circle cx="20" cy="20" r="20" fill="#dbdbdb"/><circle cx="20" cy="16" r="8" fill="#fff"/><ellipse cx="20" cy="36" rx="12" ry="8" fill="#fff"/></svg>'
  )

export default {
  components: { InstagramPreview },
  mixins: [i18nPage],
  data() {
    return {
      post: null,
      analytics: null,
      loading: true,
      publishing: false,
      deleting: false,
      activeMediaIndex: 0,
      pollTimer: null,
      commentsPayload: null,
      commentsLoading: false,
      commentsError: '',
      commentsLoaded: false,
      automationForm: {
        enabled: false,
        reply_message: 'Thanks for your comment!',
        trigger_keywords: '',
      },
      automationSaving: false,
      replyingCommentId: '',
      replyMessage: '',
      replySending: false,
    }
  },
  computed: {
    canLoadComments() {
      return this.post?.status === 'published' && !!this.post?.meta_media_id
    },
    pageTitle() {
      if (!this.post) return this.$t('posts.detail_title')
      const user = this.post.social_account?.username
      return user ? `@${user}` : this.$t('posts.detail_title')
    },
    pageSubtitle() {
      if (!this.post) return ''
      return this.$t('posts.detail_subtitle', {
        id: this.post.id,
        username: this.post.social_account?.username || '-',
      })
    },
    canEdit() {
      return this.post && !['published', 'publishing'].includes(this.post.status)
    },
    canPublish() {
      return this.post && ['draft', 'scheduled', 'failed'].includes(this.post.status)
    },
    canDelete() {
      return this.post?.status === 'failed'
    },
    previewUsername() {
      return this.post?.social_account?.username || 'username'
    },
    avatarUrl() {
      return this.post?.social_account?.profile_picture_url || DEFAULT_AVATAR
    },
    previewMediaSrc() {
      return this.post?.media_urls?.[this.activeMediaIndex] || this.post?.media_urls?.[0] || ''
    },
    previewIsVideo() {
      return this.isVideoUrl(this.previewMediaSrc)
    },
    hasMultipleMedia() {
      return (this.post?.media_urls?.length || 0) > 1
    },
    calendarLink() {
      if (!this.post?.scheduled_at) return '/posts/calendar'
      const d = new Date(this.post.scheduled_at)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `/posts/calendar?date=${y}-${m}-${day}`
    },
    statusClass() {
      const map = {
        published: 'badge-success',
        scheduled: 'badge-primary',
        failed: 'badge-danger',
        pending_approval: 'badge-warning',
        publishing: 'badge-info',
        draft: 'badge-secondary',
      }
      return map[this.post?.status] || 'badge-secondary'
    },
    metrics() {
      if (!this.analytics) return []
      const a = this.analytics
      return [
        { key: 'impressions', label: this.$t('dashboard.impressions'), value: a.impressions, icon: 'far fa-eye' },
        { key: 'reach', label: this.$t('posts.reach'), value: a.reach, icon: 'fas fa-bullseye' },
        { key: 'engagement', label: this.$t('posts.engagement'), value: a.engagement, icon: 'fas fa-chart-line' },
        { key: 'likes', label: this.$t('posts.likes'), value: a.likes, icon: 'far fa-heart' },
        { key: 'comments', label: this.$t('posts.comments'), value: a.comments, icon: 'far fa-comment' },
        { key: 'saves', label: this.$t('posts.saves'), value: a.saves, icon: 'far fa-bookmark' },
      ]
    },
    timelineSteps() {
      if (!this.post) return []
      const p = this.post
      return [
        {
          key: 'created',
          label: this.$t('posts.created'),
          date: this.formatDate(p.created_at),
          done: true,
        },
        {
          key: 'scheduled',
          label: this.$t('posts.scheduled'),
          date: p.scheduled_at ? this.formatDate(p.scheduled_at) : '—',
          done: !!p.scheduled_at,
        },
        {
          key: 'published',
          label: this.$t('posts.published_at'),
          date: p.published_at ? this.formatDate(p.published_at) : '—',
          done: !!p.published_at,
        },
      ]
    },
  },
  async created() {
    await this.loadPost()
    this.syncPoll()
  },
  beforeDestroy() {
    this.stopPoll()
  },
  methods: {
    ...mapActions('posts', ['fetchPost', 'fetchPostComments', 'publishPost', 'deletePost']),
    async loadPost() {
      this.loading = true
      try {
        const data = await this.fetchPost(this.$route.params.id)
        this.post = data.post
        this.analytics = data.analytics
        this.syncAutomationForm()
        this.activeMediaIndex = 0
        this.commentsPayload = null
        this.commentsError = ''
        this.commentsLoaded = false
      } finally {
        this.loading = false
      }
      this.syncPoll()
      if (this.canLoadComments) {
        this.loadComments()
      }
    },
    syncAutomationForm() {
      const p = this.post
      if (!p) return
      this.automationForm = {
        enabled: !!p.comment_auto_reply_enabled,
        reply_message: p.comment_auto_reply_message || 'Thanks for your comment!',
        trigger_keywords: p.comment_auto_reply_keywords || '',
      }
    },
    async saveCommentAutomation() {
      if (!this.post?.id) return
      this.automationSaving = true
      try {
        const { data } = await updateCommentAutomation(this.post.id, {
          enabled: this.automationForm.enabled,
          reply_message: this.automationForm.reply_message,
          trigger_keywords: this.automationForm.trigger_keywords,
        })
        const payload = data.data || data
        if (payload.post) this.post = { ...this.post, ...payload.post }
        this.syncAutomationForm()
        this.$toast.success(this.$t('posts.comment_automation_saved'))
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('posts.comment_automation_save_failed'))
      } finally {
        this.automationSaving = false
      }
    },
    startCommentReply(commentId) {
      this.replyingCommentId = commentId
      this.replyMessage = this.automationForm.reply_message || 'Thanks for your comment!'
    },
    cancelCommentReply() {
      this.replyingCommentId = ''
      this.replyMessage = ''
    },
    async sendCommentReply(commentId) {
      const message = (this.replyMessage || '').trim()
      if (!message || !this.post?.id) return
      this.replySending = true
      try {
        await replyPostComment(this.post.id, { comment_id: commentId, message })
        this.$toast.success(this.$t('posts.comment_reply_sent'))
        this.cancelCommentReply()
        await this.loadComments()
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('posts.comment_reply_failed'))
      } finally {
        this.replySending = false
      }
    },
    async loadComments() {
      if (!this.canLoadComments) return
      this.commentsLoading = true
      this.commentsError = ''
      try {
        this.commentsPayload = await this.fetchPostComments(this.post.id)
        this.commentsLoaded = true
      } catch (e) {
        this.commentsError = e.response?.data?.message || this.$t('posts.comments_load_failed')
      } finally {
        this.commentsLoading = false
      }
    },
    formatCommentDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString(this.intlLocale)
    },
    prevMedia() {
      if (this.activeMediaIndex > 0) this.activeMediaIndex -= 1
    },
    nextMedia() {
      if (this.post?.media_urls && this.activeMediaIndex < this.post.media_urls.length - 1) {
        this.activeMediaIndex += 1
      }
    },
    async copyCaption() {
      if (!this.post?.caption) return
      try {
        await navigator.clipboard.writeText(this.post.caption)
        this.$toast.success(this.$t('posts.caption_copied'))
      } catch {
        this.$toast.error(this.$t('storage.copy_failed'))
      }
    },
    syncPoll() {
      if (this.post?.status === 'publishing') {
        this.startPoll()
      } else {
        this.stopPoll()
      }
    },
    startPoll() {
      if (this.pollTimer) return
      this.pollTimer = setInterval(async () => {
        if (!this.post || this.post.status !== 'publishing') {
          this.stopPoll()
          return
        }
        try {
          const data = await this.fetchPost(this.$route.params.id)
          this.post = data.post
          this.analytics = data.analytics
          if (this.post.status !== 'publishing') {
            this.stopPoll()
          }
        } catch (_) {
          /* ignore transient poll errors */
        }
      }, 3000)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleString(this.intlLocale) : '-'
    },
    formatNum(n) {
      if (n == null || n === 0) return '0'
      if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
      return n.toLocaleString()
    },
    isVideoUrl(url) {
      return url && /\.(mp4|mov|webm)(\?|$)/i.test(url)
    },
    isImageUrl(url) {
      return url && !this.isVideoUrl(url)
    },
    async onPublish() {
      const ok = await this.$confirm(this.$t('posts.publish_confirm'), {
        confirmText: this.$t('common.publish'),
      })
      if (!ok) return
      this.publishing = true
      try {
        const post = await this.publishPost(this.post.id)
        this.post = post
        this.syncPoll()
        this.$toast.success(this.$t('posts.publish_queued'))
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('posts.publish_failed'))
      } finally {
        this.publishing = false
      }
    },
    async onDelete() {
      const ok = await this.$confirm(this.$t('posts.delete_confirm'), {
        confirmText: this.$t('common.delete'),
        danger: true,
      })
      if (!ok) return
      this.deleting = true
      try {
        await this.deletePost(this.post.id)
        this.$router.push('/posts')
      } catch (e) {
        this.$toast.error(e.response?.data?.message || this.$t('posts.delete_failed'))
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.post-detail__row {
  align-items: flex-start;
}

.post-detail__stats {
  margin-left: -0.25rem;
  margin-right: -0.25rem;
}

.post-stat {
  padding: 0.65rem 0.5rem;
  border: 1px solid #ececec;
  border-radius: 10px;
  background: #fff;
  text-align: center;
  height: 100%;
}

.post-stat__icon {
  color: var(--wc-text-muted);
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.post-stat__value {
  font-weight: 700;
  font-size: 1.1rem;
  line-height: 1.2;
}

.post-stat__label {
  font-size: 0.68rem;
  color: var(--wc-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.post-detail__hero {
  margin-bottom: 1rem;
}

.post-detail__badges .badge {
  margin-right: 0.35rem;
  margin-bottom: 0.35rem;
}

.post-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
}

.post-timeline li {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 140px;
}

.post-timeline__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d0d0d0;
  margin-top: 0.35rem;
  flex-shrink: 0;
}

.post-timeline__item--done .post-timeline__dot {
  background: var(--wc-primary, #1a1a1a);
}

.post-timeline__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-text-muted);
  font-weight: 600;
}

.post-timeline__date {
  font-size: 0.85rem;
}

.post-caption {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
}

.post-ai-prompt {
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  background: #f8f9fa;
}

.post-account {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.post-account__avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.post-account__avatar--ph {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045);
  color: #fff;
  font-size: 1.25rem;
}

.post-meta dt {
  font-size: 0.78rem;
  color: var(--wc-text-muted);
  font-weight: 500;
}

.post-meta dd {
  font-size: 0.88rem;
  margin-bottom: 0.75rem;
}

.post-detail__carousel {
  max-width: 300px;
  margin: 0.75rem auto 0;
}

.post-detail__carousel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.post-detail__carousel-nav {
  display: flex;
  gap: 0.35rem;
}

.post-detail__thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.post-detail__thumb {
  width: 56px;
  height: 56px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f3f3;
}

.post-detail__thumb--active {
  border-color: #1a1a1a;
}

.post-detail__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.post-detail__thumb-video {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 0.85rem;
}

.post-comments__section-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--wc-text-muted);
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.post-comments__metric {
  padding: 0.5rem 0.65rem;
  border: 1px solid #ececec;
  border-radius: 8px;
  text-align: center;
  height: 100%;
}

.post-comments__metric strong {
  display: block;
  font-size: 1.05rem;
}

.post-comments__metric span {
  font-size: 0.68rem;
  color: var(--wc-text-muted);
}

.post-comments__list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 360px;
  overflow-y: auto;
}

.post-comments__item {
  padding: 0.65rem 0;
  border-top: 1px solid #eee;
}

.post-comments__item-head {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.post-comments__text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
  font-size: 0.9rem;
}
</style>
